
import React, { useState, useContext, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { useAppContext } from '../AppContext';
import { PageContext } from '../PageContext';
import { Page } from '../types';

const SignupPage: React.FC = () => {
    const { signup } = useAuth();
    const { appConfig } = useAppContext();
    const pageContext = useContext(PageContext);
    
    const [signupMethod, setSignupMethod] = useState<'email' | 'phone'>('email');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [isAgeVerified, setIsAgeVerified] = useState(false);
    const [tempBirthYear, setTempBirthYear] = useState('');
    const [ageError, setAgeError] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);

    useEffect(() => {
        const verified = localStorage.getItem('age_verified_13');
        if (verified === 'true') {
            setIsAgeVerified(true);
        }
    }, []);

    const switchMethod = (method: 'email' | 'phone') => {
        setSignupMethod(method);
        setError('');
        setMessage('');
    };

    const handleVerifyAge = (e: React.FormEvent) => {
        e.preventDefault();
        setAgeError('');
        const year = parseInt(tempBirthYear, 10);
        if (isNaN(year) || tempBirthYear.length !== 4) {
            setAgeError('Please enter a valid 4-digit birth year (e.g. 1995).');
            return;
        }

        const currentYear = new Date().getFullYear();
        if (year < 1900 || year > currentYear) {
            setAgeError('Please enter a valid birth year.');
            return;
        }

        const age = currentYear - year;
        if (age < 13) {
            setAgeError('You must be at least 13 years old to sign up.');
            return;
        }

        localStorage.setItem('age_verified_13', 'true');
        setIsAgeVerified(true);
    };

    const handleSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        setIsLoading(true);
        try {
            await signup(email, password, displayName, '2000-01-01');
            setTimeout(() => {
                 pageContext?.switchPage(Page.Home, 'Home');
            }, 500);
        } catch (err: any) {
             console.error("Signup Page Error:", err);
            switch (err.code) {
                case 'auth/email-already-in-use':
                    setError('This email address is already taken.');
                    break;
                case 'auth/invalid-email':
                    setError('Please enter a valid email address.');
                    break;
                case 'auth/weak-password':
                    setError('Password should be at least 6 characters.');
                    break;
                case 'auth/operation-not-allowed':
                    setError('Email/Password sign-in is not enabled in Firebase Console.');
                    break;
                default:
                    setError(err.message || 'An unexpected error occurred. Please try again.');
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };
    
    const handlePhoneSignup = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        setIsLoading(true);
        try {
            const formattedPhone = phoneNumber.startsWith('+') ? phoneNumber : `+1${phoneNumber}`;
            const synthesizedEmail = `${formattedPhone.replace(/\+/g, '')}@phone.whatwasit.app`;
            await signup(synthesizedEmail, password, displayName, '2000-01-01');
            setTimeout(() => {
                 pageContext?.switchPage(Page.Home, 'Home');
            }, 500);
        } catch (err: any) {
            console.error("Phone Signup Error:", err);
            switch (err.code) {
                case 'auth/email-already-in-use':
                    setError('This phone number is already registered.');
                    break;
                case 'auth/weak-password':
                    setError('Password should be at least 6 characters.');
                    break;
                default:
                    setError(err.message || 'An unexpected error occurred. Please try again.');
                    break;
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="login-bg min-h-[100dvh] w-full flex flex-col items-center p-4 pb-12 relative overflow-y-auto">
            <style>{`
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .form-slide-up {
                    animation: slideUp 0.8s ease-out forwards;
                }
                @keyframes spin-bounce {
                    0% { transform: translateY(0) rotate(0deg); }
                    25% { transform: translateY(-10px) rotate(90deg); }
                    50% { transform: translateY(0) rotate(180deg); }
                    75% { transform: translateY(-10px) rotate(270deg); }
                    100% { transform: translateY(0) rotate(360deg); }
                }
                .animate-spin-bounce {
                    animation: spin-bounce 6s infinite ease-in-out;
                }
            `}</style>

            {!isAgeVerified ? (
                <div className="text-center text-white max-w-sm w-full my-auto mt-8 flex flex-col items-center">
                    {appConfig.logoUrl && (
                        <img 
                            src={appConfig.logoUrl} 
                            alt="Company Logo" 
                            className="w-36 h-36 mx-auto mb-6 object-contain drop-shadow-[0_0_15px_rgba(236,72,153,0.4)] animate-spin-bounce" 
                            referrerPolicy="no-referrer" 
                        />
                    )}
                    <h2 className="text-3xl font-extrabold mb-2 text-white" style={{ fontFamily: "'Caveat', cursive" }}>
                        <span className="aurora-text shimmering-text text-4xl">Are you ready to play?</span>
                    </h2>
                    <p className="text-sm text-gray-300 mb-6 max-w-xs mx-auto">
                        Please verify your birth year to confirm you are 13 or older. Your age is verified locally on this device and never stored.
                    </p>

                    <form onSubmit={handleVerifyAge} className="space-y-4 w-full">
                        <input
                            type="tel"
                            value={tempBirthYear}
                            onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '');
                                if (val.length <= 4) setTempBirthYear(val);
                            }}
                            placeholder="YYYY"
                            maxLength={4}
                            className="w-full text-center text-2xl font-bold bg-white text-gray-900 placeholder-gray-400 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-4 focus:ring-pink-500/50 tracking-widest"
                            aria-label="Birth Year Verification"
                            required
                        />
                        {ageError && (
                            <div className="bg-red-500/20 border border-red-500/40 p-2 rounded-lg text-red-200 text-xs font-bold animate-pulse">
                                {ageError}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={tempBirthYear.length < 4}
                            className="w-full bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg transform transition-transform hover:scale-105 active:scale-95 disabled:from-gray-400 disabled:to-gray-500 disabled:scale-100 flex items-center justify-center gap-2"
                        >
                            Verify & Continue <i className="ph-bold ph-arrow-right"></i>
                        </button>
                    </form>

                    <p className="text-sm text-gray-400 mt-8">
                        Already have an account?{' '}
                        <button
                            onClick={() => pageContext?.switchPage(Page.Login, 'Sign In')}
                            className="font-bold text-pink-400 hover:underline"
                        >
                            Sign In
                        </button>
                    </p>
                </div>
            ) : (
                <div className="text-center text-white max-w-sm w-full my-auto mt-8">
                    {appConfig.logoUrl && <img src={appConfig.logoUrl} alt="Company Logo" className="w-48 mx-auto mb-4 object-contain drop-shadow-2xl" referrerPolicy="no-referrer" />}
                    <h1 className="text-4xl sm:text-5xl font-extrabold mb-1" style={{ fontFamily: "'Caveat', cursive" }}>
                        <span className="aurora-text shimmering-text">Create Account</span>
                    </h1>
                    <p className="text-base text-gray-300 mb-4">
                        Join the puzzle community!
                    </p>

                    <div className="flex justify-center mb-4 space-x-4">
                        <button
                            type="button"
                            onClick={() => switchMethod('email')}
                            className={`px-4 py-2 rounded-full font-bold transition-colors ${signupMethod === 'email' ? 'bg-pink-500 text-white' : 'bg-black/20 text-gray-300 hover:bg-black/40'}`}
                        >
                            Email
                        </button>
                        <button
                            type="button"
                            onClick={() => switchMethod('phone')}
                            className={`px-4 py-2 rounded-full font-bold transition-colors ${signupMethod === 'phone' ? 'bg-pink-500 text-white' : 'bg-black/20 text-gray-300 hover:bg-black/40'}`}
                        >
                            Phone
                        </button>
                    </div>

                    {signupMethod === 'email' ? (
                    <form onSubmit={handleSignup} className="space-y-4 form-slide-up">
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            placeholder="Display Name (e.g. Alex G)"
                            className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                            aria-label="Display Name"
                            required
                        />
                        
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email Address"
                            className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                            aria-label="Email Address"
                            required
                        />
                        <div className="relative w-full">
                            <input
                                type={isPasswordVisible ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Password (min. 6 characters)"
                                className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg pr-10 focus:outline-none focus:ring-2 focus:ring-pink-500"
                                aria-label="Password"
                                required
                            />
                             <button
                                type="button"
                                onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                            >
                                <i className={`ph-duotone ${isPasswordVisible ? 'ph-eye-slash' : 'ph-eye'} text-xl`}></i>
                            </button>
                        </div>

                        {error && <div className="bg-red-500/20 border border-red-500 p-2 rounded text-red-200 text-sm font-bold">{error}</div>}
                        
                        <div className="flex items-start mb-4">
                            <input
                                type="checkbox"
                                id="tos-checkbox"
                                className="mt-1 mr-2 text-pink-500 focus:ring-pink-500 border-gray-300 rounded"
                                required
                            />
                            <label htmlFor="tos-checkbox" className="text-sm text-gray-300 text-left">
                                I agree to the{" "}
                                <span 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        pageContext?.switchPage(Page.TermsOfService, "Terms of Service");
                                    }}
                                    className="text-pink-400 hover:underline cursor-pointer"
                                >
                                    Terms of Service
                                </span>, including our Zero Tolerance Policy for objectionable content.
                            </label>
                        </div>

                        <button
                            type="submit"
                            disabled={isLoading || !displayName || !email || !password}
                            className="w-full bg-white text-purple-700 font-bold py-3 px-6 rounded-lg shadow-lg transform transition-transform hover:scale-105 disabled:bg-gray-300 disabled:scale-100 flex items-center justify-center"
                        >
                            {isLoading ? <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.5)', borderLeftColor: 'var(--primary-dark-purple)'}}></div> : 'Sign Up & Play'}
                        </button>
                    </form>
                    ) : (
                    <div className="space-y-4 form-slide-up">
                            <form onSubmit={handlePhoneSignup} className="space-y-4">
                                <input
                                    type="text"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    placeholder="Display Name (e.g. Alex G)"
                                    className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    aria-label="Display Name"
                                    required
                                />
                                <input
                                    type="tel"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    placeholder="Phone Number (e.g. +1...)"
                                    className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                    aria-label="Phone Number"
                                    required
                                />
                                <div className="relative">
                                    <input
                                        type={isPasswordVisible ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Create password"
                                        className="w-full bg-white text-gray-900 placeholder-gray-500 py-3 px-4 rounded-lg shadow-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                                        aria-label="Create password"
                                        required
                                        minLength={6}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                                    >
                                        <i className={`ph-duotone ${isPasswordVisible ? 'ph-eye-slash' : 'ph-eye'} text-xl`}></i>
                                    </button>
                                </div>
                                
                                <div className="flex items-start mb-4">
                                    <input
                                        type="checkbox"
                                        id="tos-checkbox-phone"
                                        className="mt-1 mr-2 text-pink-500 focus:ring-pink-500 border-gray-300 rounded"
                                        required
                                    />
                                    <label htmlFor="tos-checkbox-phone" className="text-sm text-gray-300 text-left">
                                        I agree to the{" "}
                                        <span 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                pageContext?.switchPage(Page.TermsOfService, "Terms of Service");
                                            }}
                                            className="text-pink-400 hover:underline cursor-pointer"
                                        >
                                            Terms of Service
                                        </span>, including our Zero Tolerance Policy for objectionable content.
                                    </label>
                                </div>

                                {error && <div className="bg-red-500/20 border border-red-500 p-2 rounded text-red-200 text-sm font-bold">{error}</div>}
                                
                                <button
                                    type="submit"
                                    disabled={isLoading || !displayName || !phoneNumber || !password}
                                    className="w-full bg-white text-purple-700 font-bold py-3 px-6 rounded-lg shadow-lg transform transition-transform hover:scale-105 disabled:bg-gray-300 disabled:scale-100 flex items-center justify-center"
                                >
                                    {isLoading ? <div className="spinner-white" style={{ borderColor: 'rgba(107, 33, 168, 0.5)', borderLeftColor: 'var(--primary-dark-purple)'}}></div> : 'Sign Up & Play'}
                                </button>
                            </form>
                    </div>
                    )}

                    <p className="text-sm text-gray-400 mt-6 form-slide-up" style={{ animationDelay: '0.7s' }}>
                        Already have an account?{' '}
                        <button
                            onClick={() => pageContext?.switchPage(Page.Login, 'Sign In')}
                            className="font-bold text-pink-400 hover:underline"
                        >
                            Sign In
                        </button>
                    </p>
                </div>
            )}
        </div>
    );
};

export default SignupPage;
